/*
 * (C) Behaviour Interactive Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium, is strictly prohibited
 * This file is proprietary and confidential
 */

import { AuthToken } from '../api/auth/auth.models';
import { ROOT_PAGE, STORAGE_KEYS } from '../constants';
import { BAError, BAErrorCode } from '../types/requests';
import { getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem } from './storage';
import { removeUserSession } from './user/session';
import { getWindowStatus } from './window';

export function setAuthToken(token: string, expiresIn: number): void {
  const expirationDate = Date.now() + expiresIn * 1000;
  setLocalStorageItem(STORAGE_KEYS.AUTH_TOKEN, token);
  setLocalStorageItem(STORAGE_KEYS.AUTH_TOKEN_EXPIRATION, expirationDate.toString());
  removeLocalStorageItem(STORAGE_KEYS.AUTH_CODE_VERIFIER);
}

export function getAuthToken(): AuthToken | null {
  if (!getWindowStatus()) {
    return null;
  }

  const token = getLocalStorageItem(STORAGE_KEYS.AUTH_TOKEN);
  const expirationDateStr = getLocalStorageItem(STORAGE_KEYS.AUTH_TOKEN_EXPIRATION);

  if (!token || !expirationDateStr) {
    return null;
  }

  const expirationDate = parseInt(expirationDateStr, 10);

  return { token, expirationDate };
}

export function clearAuthToken(): void {
  removeLocalStorageItem(STORAGE_KEYS.AUTH_TOKEN);
  removeLocalStorageItem(STORAGE_KEYS.AUTH_TOKEN_EXPIRATION);
}

export async function generateProofKey() {
  if (!getWindowStatus()) {
    return null;
  }

  const generateVerifier = (length = 43): string => {
    return Buffer.from(crypto.getRandomValues(new Uint8Array(length))).toString('hex');
  };

  const generateChallenge = async (verifier: string): Promise<string> => {
    const hashedValue = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    const base64 = Buffer.from(hashedValue).toString('base64');
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  };

  try {
    const verifier = generateVerifier();
    const challenge = await generateChallenge(verifier);
    setLocalStorageItem(STORAGE_KEYS.AUTH_CODE_VERIFIER, verifier);
    return { verifier, challenge };
  } catch (error) {
    console.error('Error generating proof key:', error);
    return null;
  }
}

export function setAuthRedirect(currentRoute: string): void {
  if (getWindowStatus()) {
    setLocalStorageItem(STORAGE_KEYS.AUTH_REDIRECT, currentRoute);
  }
}

export function getAuthRedirect(): string {
  const defaultPath = ROOT_PAGE;

  if (!getWindowStatus()) {
    return defaultPath;
  }

  return getLocalStorageItem(STORAGE_KEYS.AUTH_REDIRECT) || defaultPath;
}

export function handleAuthError(error: BAError): void {
  const authToken = getAuthToken();
  const { message, code } = error;

  if (!authToken || !authToken.token) {
    return;
  }

  if (
    (code === BAErrorCode.SINGLE_SIGN_ON_GENERIC_ERROR && message === 'Access Token is revoked') ||
    (code === BAErrorCode.GENERIC_UNAUTHORIZED && message === 'Authentication required')
  ) {
    void removeUserSession();
  }
}
